import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import BlockEditor from '../../components/ui/BlockEditor';
import { useAuth } from '../../context/AuthContext';
import { 
  Newspaper, Plus, Edit, Trash2, Eye, Camera, Loader2, 
  Calendar, ExternalLink, Image as ImageIcon, X, FolderOpen
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const categories = [
  { value: 'class_photo', label: 'Class Photos' },
  { value: 'news', label: 'News & Updates' },
  { value: 'success_story', label: 'Success Stories' },
  { value: 'event', label: 'Events' },
  { value: 'announcement', label: 'Announcements' },
];

const statuses = [
  { value: 'draft', label: 'Draft' },
  { value: 'published', label: 'Published' },
  { value: 'archived', label: 'Archived' },
];

export default function AdminBlogPage() {
  const { api, token } = useAuth();
  const [posts, setPosts] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    content: '',
    content_blocks: [],
    featured_image_url: '',
    category: 'news',
    status: 'draft',
    related_course: '',
    tags: '',
    photo_gallery: [],
  });
  const [newPhotoUrl, setNewPhotoUrl] = useState('');
  const [newPhotoCaption, setNewPhotoCaption] = useState('');
  
  // Media Library state
  const [mediaModalOpen, setMediaModalOpen] = useState(false);
  const [mediaAssets, setMediaAssets] = useState([]);
  const [mediaCallback, setMediaCallback] = useState(null);

  // Fetch media assets
  const fetchMediaAssets = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/cms/media/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setMediaAssets(data.results || data);
      }
    } catch (error) {
      console.error('Error fetching media:', error);
    }
  }, [token]);

  // Open media selector
  const openMediaSelector = (callback) => {
    setMediaCallback(() => callback);
    fetchMediaAssets();
    setMediaModalOpen(true);
  };

  // Select media
  const selectMedia = (url) => {
    if (mediaCallback) {
      mediaCallback(url);
    }
    setMediaModalOpen(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [postsRes, coursesRes] = await Promise.all([
        api.get('/blog/'),
        api.get('/courses/')
      ]);
      setPosts(postsRes.data.results || postsRes.data || []);
      setCourses(coursesRes.data.results || coursesRes.data || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load blog posts');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      excerpt: '',
      content: '',
    content_blocks: [],
      featured_image_url: '',
      category: 'news',
      status: 'draft',
      related_course: 'none',
      tags: '',
      photo_gallery: [],
    });
    setEditingPost(null);
    setNewPhotoUrl('');
    setNewPhotoCaption('');
  };

  const handleOpenDialog = (post = null) => {
    if (post) {
      setEditingPost(post);
      setFormData({
        title: post.title || '',
        excerpt: post.excerpt || '',
        content: post.content || '',
        content_blocks: post.content_blocks || [],
        featured_image_url: post.featured_image_url || '',
        category: post.category || 'news',
        status: post.status || 'draft',
        related_course: post.related_course || '',
        tags: post.tags?.join(', ') || '',
        photo_gallery: post.photo_gallery || [],
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleAddPhoto = () => {
    if (!newPhotoUrl) return;
    setFormData(prev => ({
      ...prev,
      photo_gallery: [...prev.photo_gallery, { url: newPhotoUrl, caption: newPhotoCaption }]
    }));
    setNewPhotoUrl('');
    setNewPhotoCaption('');
  };

  const handleRemovePhoto = (index) => {
    setFormData(prev => ({
      ...prev,
      photo_gallery: prev.photo_gallery.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || (!formData.content && (!formData.content_blocks || formData.content_blocks.length === 0))) {
      toast.error('Please fill in title and content');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        title: formData.title,
        excerpt: formData.excerpt || null,
        content: formData.content,
        content_blocks: formData.content_blocks,
        featured_image_url: formData.featured_image_url || null,
        category: formData.category,
        status: formData.status || 'published',
        related_course: formData.related_course === 'none' ? null : formData.related_course,
        tags: formData.tags.split(',').map(t => t.trim()).filter(Boolean),
        photo_gallery: formData.photo_gallery,
      };

      if (editingPost) {
        await api.put(`/blog/${editingPost.slug}/`, payload);
        toast.success('Post updated successfully!');
      } else {
        await api.post('/blog/', payload);
        toast.success('Post created successfully!');
      }
      await fetchData();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      console.error('Save error:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Failed to save post');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (post) => {
    if (!window.confirm(`Are you sure you want to delete "${post.title}"?`)) return;
    
    try {
      await api.delete(`/blog/${post.slug}/`);
      toast.success('Post deleted');
      await fetchData();
    } catch (error) {
      toast.error('Failed to delete post');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not published';
    return new Date(dateString).toLocaleDateString();
  };

  // Stats
  const publishedCount = posts.filter(p => p.status === 'published').length;
  const draftCount = posts.filter(p => p.status === 'draft').length;
  const classPhotoCount = posts.filter(p => p.category === 'class_photo').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Blog Management</h1>
          <p className="text-muted-foreground">Manage blog posts and class photos</p>
        </div>
        <Button onClick={() => handleOpenDialog()} data-testid="add-post-btn">
          <Plus className="h-4 w-4 mr-2" />
          New Post
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="card-dashboard">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <Newspaper className="h-8 w-8 text-primary" />
              <div>
                <p className="text-2xl font-bold">{posts.length}</p>
                <p className="text-sm text-muted-foreground">Total Posts</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-dashboard">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <Eye className="h-8 w-8 text-green-500" />
              <div>
                <p className="text-2xl font-bold">{publishedCount}</p>
                <p className="text-sm text-muted-foreground">Published</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-dashboard">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <Edit className="h-8 w-8 text-amber-500" />
              <div>
                <p className="text-2xl font-bold">{draftCount}</p>
                <p className="text-sm text-muted-foreground">Drafts</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-dashboard">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <Camera className="h-8 w-8 text-purple-500" />
              <div>
                <p className="text-2xl font-bold">{classPhotoCount}</p>
                <p className="text-sm text-muted-foreground">Class Photos</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Posts List */}
      <Card>
        <CardHeader>
          <CardTitle>All Posts</CardTitle>
        </CardHeader>
        <CardContent>
          {posts.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Newspaper className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No blog posts yet. Create your first post!</p>
            </div>
          ) : (
            <div className="space-y-4">
              {posts.map(post => (
                <div key={post.id} className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold">{post.title}</h3>
                      <Badge variant={post.status === 'published' ? 'default' : 'secondary'}>
                        {post.status}
                      </Badge>
                      <Badge variant="outline">{post.category.replace('_', ' ')}</Badge>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {formatDate(post.published_at)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Eye className="h-3 w-3" />
                        {post.view_count} views
                      </span>
                      {post.photo_gallery?.length > 0 && (
                        <span className="flex items-center gap-1">
                          <Camera className="h-3 w-3" />
                          {post.photo_gallery.length} photos
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {post.status === 'published' && (
                      <Button variant="ghost" size="sm" asChild>
                        <a href={`/blog/${post.slug}`} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => handleOpenDialog(post)}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(post)}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingPost ? 'Edit Post' : 'Create New Post'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Title *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Post title"
                />
              </div>
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Excerpt</Label>
              <Input
                value={formData.excerpt}
                onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                placeholder="Short summary for previews"
              />
            </div>

            <div className="space-y-2">
              <Label>Content *</Label>
              <BlockEditor 
                blocks={formData.content_blocks}
                onChange={(blocks) => setFormData({ ...formData, content_blocks: blocks })}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {statuses.map(s => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Related Course</Label>
                <Select value={formData.related_course} onValueChange={(v) => setFormData({ ...formData, related_course: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select course (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {courses.map(course => (
                      <SelectItem key={course.id} value={course.id}>{course.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Featured Image</Label>
              <div className="flex gap-2">
                <Input
                  value={formData.featured_image_url}
                  onChange={(e) => setFormData({ ...formData, featured_image_url: e.target.value })}
                  placeholder="https://... or select from media library"
                  className="flex-1"
                />
                <Button type="button" variant="outline" onClick={() => openMediaSelector((url) => setFormData({ ...formData, featured_image_url: url }))}>
                  <FolderOpen className="h-4 w-4 mr-1" /> Media
                </Button>
              </div>
              {formData.featured_image_url && (
                <div className="relative w-32 h-20 rounded overflow-hidden border mt-2">
                  <img src={formData.featured_image_url} alt="Featured" className="w-full h-full object-cover" />
                  <button type="button" onClick={() => setFormData({ ...formData, featured_image_url: '' })} className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-0.5">
                    <X className="h-3 w-3" />
                  </button>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label>Tags (comma-separated)</Label>
              <Input
                value={formData.tags}
                onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                placeholder="python, kids, coding"
              />
            </div>

            {/* Photo Gallery */}
            {formData.category === 'class_photo' && (
              <div className="space-y-3 p-4 border rounded-lg bg-muted/30">
                <Label className="flex items-center gap-2">
                  <Camera className="h-4 w-4" />
                  Photo Gallery
                </Label>
                
                {formData.photo_gallery.length > 0 && (
                  <div className="grid grid-cols-3 gap-2">
                    {formData.photo_gallery.map((photo, idx) => (
                      <div key={idx} className="relative group">
                        <div className="aspect-square bg-muted rounded overflow-hidden">
                          <img src={photo.url} alt={photo.caption} className="w-full h-full object-cover" 
                            onError={(e) => { e.target.src = 'https://placehold.co/100x100?text=?'; }} />
                        </div>
                        <button
                          type="button"
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={() => handleRemovePhoto(idx)}
                        >
                          <X className="h-3 w-3" />
                        </button>
                        {photo.caption && (
                          <p className="text-xs text-muted-foreground truncate mt-1">{photo.caption}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex gap-2">
                  <Input
                    placeholder="Photo URL"
                    value={newPhotoUrl}
                    onChange={(e) => setNewPhotoUrl(e.target.value)}
                    className="flex-1"
                  />
                  <Input
                    placeholder="Caption (optional)"
                    value={newPhotoCaption}
                    onChange={(e) => setNewPhotoCaption(e.target.value)}
                    className="flex-1"
                  />
                  <Button type="button" variant="outline" onClick={() => openMediaSelector((url) => setNewPhotoUrl(url))}>
                    <FolderOpen className="h-4 w-4" />
                  </Button>
                  <Button type="button" variant="outline" onClick={handleAddPhoto}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {editingPost ? 'Update Post' : 'Create Post'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Media Library Modal */}
      <Dialog open={mediaModalOpen} onOpenChange={setMediaModalOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle>Select from Media Library</DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto max-h-[60vh] p-4">
            {mediaAssets.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <ImageIcon className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No images in media library</p>
                <p className="text-sm">Upload images in Website Content → Media Library</p>
              </div>
            ) : (
              <div className="grid grid-cols-4 gap-4">
                {mediaAssets.filter(m => m.asset_type === 'image').map(media => (
                  <button
                    key={media.id}
                    onClick={() => selectMedia(media.url || media.file_url || media.file)}
                    className="border rounded-lg overflow-hidden hover:border-primary hover:ring-2 ring-primary/20 transition-all"
                  >
                    <img 
                      src={media.url || media.file_url || media.file} 
                      alt={media.name}
                      className="w-full h-24 object-cover"
                    />
                    <p className="text-xs p-2 truncate text-muted-foreground">{media.name}</p>
                  </button>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
